import { Map } from 'ol';
import { get as getProjection, transformExtent } from 'ol/proj';
import XYZ from 'ol/source/XYZ';
import { createXYZ } from 'ol/tilegrid';
import { v4 } from 'uuid';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { assertEnvVariable } from '../../../../utils/environment';
import { WmsSource } from '.';

interface AuthOptions {
  authType: 'anonymous';
  clientId: string;
}

const constants = {
  AUTHORIZATION: 'authorization',
  AUTHORIZATION_SCHEME: 'Bearer',
  MAP_AGENT: 'Map-Agent',
  MS_AM_REQUEST_ORIGIN: 'Ms-Am-Request-Origin',
  MS_AM_REQUEST_ORIGIN_VALUE: 'MapControl',
  X_MS_CLIENT_ID: 'x-ms-client-id',
  SESSION_ID: 'Session-Id',
  SDK_VERSION: '0.0.1',
  TARGET_SDK: 'OpenLayers',
};

// Imagery max zoom typically 19.
const MAX_ZOOM_LEVEL = 19;

export class AzureMapsSource extends WmsSource {
  private _token: string | null = null;
  private _tokenExpiry: number | null = null;
  private authOptions!: AuthOptions;
  private tilesetId = 'microsoft.imagery';

  // We'll generate a session ID once per instance.
  private static sessionId: string = v4();

  async initialize() {
    this.authOptions = {
      authType: 'anonymous',
      clientId: assertEnvVariable('AZURE_MAPS_CLIENT_ID'),
    };

    // Fetch initial token.
    await this._fetchToken();

    // Create the XYZ source configured for Azure Maps.
    this.source = new XYZ({
      projection: 'EPSG:3857',
      tileGrid: createXYZ({ maxZoom: MAX_ZOOM_LEVEL }),
      tileLoadFunction: (imageTile, src) => this._loadTile(imageTile, src),
      url: this._buildTileUrl(),
      crossOrigin: 'anonymous',
      transition: 0,
    });

    return Promise.resolve();
  }

  async updateAttributions(olMap: Map): Promise<void> {
    const zoom = olMap.getView().getZoom();
    // Extent in EPSG:3857.
    const extent = olMap.getView().calculateExtent(olMap.getSize());
    // Convert to EPSG:4326 for Azure Maps to WGS84.
    const transformed = transformExtent(
      extent,
      getProjection('EPSG:3857')!,
      getProjection('EPSG:4326')!,
    );

    // Typically: [minLon, minLat, maxLon, maxLat]
    const boundsParam = `${transformed[0]},${transformed[1]},${transformed[2]},${transformed[3]}`;

    const url =
      `https://atlas.microsoft.com/map/attribution?api-version=2.1` +
      `&tilesetId=microsoft.imagery` +
      `&zoom=${Math.floor(Math.min(zoom ?? 0, MAX_ZOOM_LEVEL))}` +
      `&bounds=${boundsParam}`;

    let request: { url: string; headers: { [key: string]: string } } = { url, headers: {} };
    request = this.signRequest(request);

    try {
      const response = await fetch(request.url, { headers: request.headers });
      if (!response.ok) {
        throw new Error(`Request error. ${response.status}: ${response.statusText}`);
      }
      const attributionData = await response.json();

      const providers = this.parseAttribution(attributionData);
      this.setAttributionUI(providers);
    } catch (err) {
      if (err instanceof TypeError) {
        console.error('Network error while fetching attributions:', err);
      } else {
        console.error('Failed to parse or set attributions:', err);
      }
    }
  }

  /**
   * Builds the Azure Maps tile URL.
   * Using the Render V2 endpoint for imagery:
   * https://atlas.microsoft.com/map/tile?api-version=2.1&tilesetId=microsoft.imagery&zoom={z}&x={x}&y={y}&tileSize=256&language=en-US&view=Auto
   */
  private _buildTileUrl(): string {
    return `https://atlas.microsoft.com/map/tile?api-version=2.1&tilesetId=${this.tilesetId}&zoom={z}&x={x}&y={y}&tileSize=256&language=en-US&view=Auto`;
  }

  /**
   * Loads a tile with the Authorization header.
   */
  private async _loadTile(imageTile: any, src: string) {
    await this._ensureValidToken();

    let request: { url: string; headers: { [key: string]: string } } = { url: src, headers: {} };
    request = this.signRequest(request);

    const xhr = new XMLHttpRequest();
    xhr.open('GET', request.url);
    if (request.headers) {
      for (const key in request.headers) {
        xhr.setRequestHeader(key, request.headers[key]);
      }
    }
    xhr.responseType = 'blob';

    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response as Blob;
        const reader = new FileReader();
        reader.onload = () => {
          imageTile.getImage().src = reader.result as string;
        };
        reader.readAsDataURL(blob);
      } else {
        console.error('Failed to load tile', xhr.status, xhr.statusText);
      }
    };

    xhr.send();
  }

  /**
   * Ensures that the current token is still valid. If expired or close to expiration, fetch a new one.
   */
  private async _ensureValidToken() {
    if (!this._token || !this._tokenExpiry || Date.now() >= this._tokenExpiry - 60000) {
      // Token is missing or expires within the next minute, refresh it.
      await this._fetchToken();
    }
  }

  /**
   * Fetches a new token using the provided getToken callback.
   */
  private async _fetchToken() {
    const authServiceV1 = SkyMapAxiosServiceFactory.instance.createAuthServiceV1();
    const { data } = await authServiceV1.createAzureMapsAccessToken({});

    this._token = data.token;
    this._tokenExpiry = data.expiresOnTimestamp!;
  }

  /**
   * Sign the request by adding headers required to fetch Azure Map images.
   */
  private signRequest(request: { url: string; headers: { [key: string]: string } }): {
    url: string;
    headers: { [key: string]: string };
  } {
    const opt = this.authOptions;
    const headers = (request.headers || {}) as { [key: string]: string };

    // Add identifying headers.
    headers[constants.SESSION_ID] = AzureMapsSource.sessionId;
    headers[constants.MS_AM_REQUEST_ORIGIN] = constants.MS_AM_REQUEST_ORIGIN_VALUE;
    headers[constants.MAP_AGENT] = `MapControl/${constants.SDK_VERSION} (${constants.TARGET_SDK})`;

    // Auth type is anonymous with a token.
    headers[constants.X_MS_CLIENT_ID] = opt.clientId;
    headers[constants.AUTHORIZATION] = `${constants.AUTHORIZATION_SCHEME} ${this._token}`;

    request.headers = headers;
    return request;
  }

  private parseAttribution(data: any): string[] {
    return data?.copyrights || [];
  }

  /**
   * Style and add the attribution to the source.
   */
  private setAttributionUI(providers: string[]) {
    const styledAttribution = `
  <div style="
    background-color: black; 
    padding: 0 4px;
    display: inline-block;
  ">
    ${providers.join(' ').replaceAll('<a ', '<a style="color: white;"')}
  </div>
`;

    this.source?.setAttributions(styledAttribution);
  }
}
